import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import JSZip from 'jszip';
import * as THREE from 'three';
import API_URL from '../../../config';
import './JbzViewer.css';
import './DxfViewer.css';
// [SAVE-LOAD-REWRITE] Ön izleme, editörün GERÇEK deserializer'larını kullanır
// (kopya deserializeNode sapıp ön izlemeyi bozuyordu). scene-objects (tesisat/wire)
// → deserializeObject; kat planı tabanı (_serializedGroup) → deserializeFloorPlanGroup.
import { deserializeObject } from '../../persistence/save/sceneSerializer';
import { deserializeFloorPlanGroup } from '../../persistence/save/projectSerializer';

// floor-plans tab ID → label
const FP_TAB_LABELS = {
  mimari: 'Mimari',
  elektrik: 'Kuvvetli Akım',
  telekom: 'Zayıf Akım',
  yangin: 'Yangın Algılama',
  santiye: 'Şantiye',
  baglanti: 'Bağlantı Hattı',
};

// floor-plans tab ID → scene-objects key
const FP_TO_SCENE = {
  elektrik: 'strong_current',
  telekom: 'weak_current',
  yangin: 'fire_detection',
  santiye: 'construction_site',
  baglanti: 'connection_line',
};

const TAB_ORDER = ['mimari', 'elektrik', 'telekom', 'yangin', 'santiye', 'baglanti'];

// ─── Callout helpers ────────────────────────────────────────────────────────

const CALLOUT_COLOR = 0x00ccff;
const CALLOUT_CSS = '#00ccff';

// Bir not için paydaş rengini (CSS hex string) çöz; yoksa default callout rengini döndür.
function noteColorCSS(note) {
  try {
    // Lazy require — circular önlemek için
    const { colorForRoleLabel } = require('../../../utils/stakeholderColors');
    const c = colorForRoleLabel(note?.authorRole);
    return c || CALLOUT_CSS;
  } catch { return CALLOUT_CSS; }
}
function noteColorInt(note) {
  const cssC = noteColorCSS(note);
  if (typeof cssC !== 'string') return CALLOUT_COLOR;
  const s = cssC.startsWith('#') ? cssC.slice(1) : cssC;
  const n = parseInt(s, 16);
  return Number.isFinite(n) ? n : CALLOUT_COLOR;
}
function withAlpha(hex, alpha) {
  const s = hex.startsWith('#') ? hex.slice(1) : hex;
  const n = parseInt(s, 16) || 0;
  const r = (n >> 16) & 0xff, g = (n >> 8) & 0xff, b = n & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

function wrapText(ctx, text, maxW) {
  const words = text.split(' ');
  const lines = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? cur + ' ' + w : w;
    if (ctx.measureText(test).width > maxW && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

// Base world-width of a callout (fraction of project view height).
// frameWidth is a multiplier on the canvas width — larger frame means the text
// wraps at a wider column and occupies fewer lines. Font size stays constant.
const CALLOUT_BASE_WIDTH_FRACTION = 0.10;
const CALLOUT_MIN_FRAME_WIDTH = 0.7;
const CALLOUT_MAX_FRAME_WIDTH = 3.0;

function makeTextSprite(text, viewH, header, frameWidth = 1, accentCss = CALLOUT_CSS) {
  const S = 4;
  const fs = 12 * S;           // body font (pixel-fixed)
  const hfs = 12 * S;          // header font — same size as body
  const pad = 6 * S;
  const stripH = hfs * 1.55;   // header bar height
  const baseMaxW = 200 * S;
  const maxW = Math.round(baseMaxW * frameWidth);
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');

  // Measure body lines
  ctx.font = `bold ${fs}px Arial`;
  const bodyLines = wrapText(ctx, text, maxW - pad * 2);
  const lh = fs * 1.3;
  const bodyH = bodyLines.length * lh;

  // Measure header (single line, truncated if too long)
  let headerText = null;
  let headerBandH = 0;
  if (header && header.trim()) {
    ctx.font = `bold ${hfs}px Arial`;
    headerText = header.trim();
    const maxHW = maxW - pad * 2;
    while (ctx.measureText(headerText).width > maxHW && headerText.length > 3) {
      headerText = headerText.slice(0, -2);
    }
    if (headerText.length < header.trim().length) headerText += '…';
    headerBandH = stripH;
  }

  c.width = maxW;
  c.height = bodyH + pad * 2 + headerBandH;

  // Background
  ctx.fillStyle = 'rgba(0,0,0,.8)';
  ctx.fillRect(0, 0, c.width, c.height);

  if (headerText) {
    ctx.fillStyle = withAlpha(accentCss, 0.22);
    ctx.fillRect(0, 0, c.width, headerBandH);
    ctx.strokeStyle = accentCss;
    ctx.lineWidth = 1 * S;
    ctx.beginPath();
    ctx.moveTo(0, headerBandH);
    ctx.lineTo(c.width, headerBandH);
    ctx.stroke();
    ctx.font = `bold ${hfs}px Arial`;
    ctx.fillStyle = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.fillText(headerText, pad, headerBandH / 2);
  }

  // Outer border
  ctx.strokeStyle = accentCss;
  ctx.lineWidth = 2 * S;
  ctx.strokeRect(0, 0, c.width, c.height);

  // Body text
  ctx.font = `bold ${fs}px Arial`;
  ctx.fillStyle = accentCss;
  ctx.textBaseline = 'top';
  bodyLines.forEach((l, i) => ctx.fillText(l, pad, headerBandH + pad + i * lh));

  // Resize handle (bottom-right triangle) — görsel ipucu
  const handleSize = 10 * S;
  ctx.fillStyle = accentCss;
  ctx.beginPath();
  ctx.moveTo(c.width, c.height);
  ctx.lineTo(c.width - handleSize, c.height);
  ctx.lineTo(c.width, c.height - handleSize);
  ctx.closePath();
  ctx.fill();

  const tex = new THREE.CanvasTexture(c);
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, alphaTest: 0.05, depthTest: false });
  const sprite = new THREE.Sprite(mat);
  // World width scales with frameWidth so the rendered text stays the same
  // physical size (font is pixel-fixed in canvas, world-size grows with canvas).
  const sw = viewH * CALLOUT_BASE_WIDTH_FRACTION * frameWidth;
  const sh = sw * (c.height / c.width);
  sprite.scale.set(sw, sh, 1);
  sprite.center.set(0, 1);
  return sprite;
}

// viewH burada "proje referans yüksekliği" (scene bounding box height) —
// camera'nın o anki zoom değeri değil. Böylece callout boyutu projeye göre
// sabit olur ve zoom'la birlikte doğal olarak büyür/küçülür.
function buildCalloutGroup(note, viewH) {
  const g = new THREE.Group();
  g.userData = { _callout: true, id: note.id };
  g.renderOrder = 999;
  // Paydaş (yazar) rengi — yoksa default cyan
  const accentCss = noteColorCSS(note);
  const accentInt = noteColorInt(note);
  // leader line
  const lg = new THREE.BufferGeometry();
  lg.setAttribute('position', new THREE.Float32BufferAttribute([
    note.tx, note.ty, 1, note.bx, note.by, 1,
  ], 3));
  const lm = new THREE.LineBasicMaterial({ color: accentInt, depthTest: false });
  const line = new THREE.Line(lg, lm);
  line.renderOrder = 999;
  g.add(line);
  // target marker — sabit 2 birim yarıçap
  const mr = 2;
  const cg = new THREE.CircleGeometry(mr, 16);
  const cm = new THREE.MeshBasicMaterial({ color: accentInt, depthTest: false });
  const circle = new THREE.Mesh(cg, cm);
  circle.position.set(note.tx, note.ty, 1);
  circle.renderOrder = 999;
  g.add(circle);
  // text sprite with author header — format: "1-İrfan Badur — İş Sahibi"
  let header = null;
  if (note.authorName || note._authorIndex) {
    const nameWithIndex = note._authorIndex
      ? `${note._authorIndex}-${note.authorName || 'Bilinmeyen'}`
      : (note.authorName || '');
    const parts = [nameWithIndex];
    if (note.authorRole) parts.push(note.authorRole);
    header = parts.filter(Boolean).join(' — ');
  }
  const rawFW = typeof note.frameWidth === 'number' && note.frameWidth > 0
    ? note.frameWidth
    : (typeof note.scale === 'number' && note.scale > 0 ? note.scale : 1);
  const fw = Math.max(CALLOUT_MIN_FRAME_WIDTH, Math.min(CALLOUT_MAX_FRAME_WIDTH, rawFW));
  const sprite = makeTextSprite(note.text, viewH, header, fw, accentCss);
  sprite.position.set(note.bx, note.by, 1);
  sprite.renderOrder = 999;
  sprite.userData = { _noteId: note.id, _frameWidth: fw, _text: note.text, _header: header };
  g.add(sprite);
  return g;
}

function screenToWorld(e, camera, el) {
  const r = el.getBoundingClientRect();
  const nx = (e.clientX - r.left) / r.width;
  const ny = (e.clientY - r.top) / r.height;
  return {
    x: camera.left + nx * (camera.right - camera.left),
    y: camera.top - ny * (camera.top - camera.bottom),
  };
}

// ─── Fit camera to scene ────────────────────────────────────────────────────

function fitCameraToScene(scene, camera, w, h) {
  scene.updateMatrixWorld(true);
  const box = new THREE.Box3();
  let hasContent = false;
  scene.traverse((child) => {
    if (child.isMesh || child.isLine || child.isLineSegments || child.isLineLoop) {
      box.expandByObject(child);
      hasContent = true;
    }
  });
  if (!hasContent || box.isEmpty()) {
    camera.position.set(0, 0, 100);
    camera.left = -500; camera.right = 500;
    camera.top = 500; camera.bottom = -500;
    camera.updateProjectionMatrix();
    return 1000; // default project height for empty scenes
  }
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const dw = size.x || 1;
  const dh = size.y || 1;
  const aspect = w / h;
  const padding = 1.1;
  let halfW, halfH;
  if (dw / dh > aspect) {
    halfW = (dw / 2) * padding;
    halfH = halfW / aspect;
  } else {
    halfH = (dh / 2) * padding;
    halfW = halfH * aspect;
  }
  camera.left = center.x - halfW;
  camera.right = center.x + halfW;
  camera.top = center.y + halfH;
  camera.bottom = center.y - halfH;
  camera.position.set(0, 0, 100);
  camera.updateProjectionMatrix();
  // Return the "natural project view height" — used as stable reference for
  // world-space callout sizing (independent of the user's current zoom level).
  return halfH * 2;
}

// ─── Component ──────────────────────────────────────────────────────────────

const JbzViewer = ({ file, url, className = '', height = 300, projectId, zoomTarget }) => {
  const containerRef = useRef(null);
  const threeRef = useRef(null); // { renderer, scene, camera, frameId }
  const dragRef = useRef({ dragging: false, lastX: 0, lastY: 0 });
  const resizeRef = useRef(null); // { noteId, startWorldX, startWorldY, startScale }

  const [floorPlans, setFloorPlans] = useState(null);
  const [sceneObjects, setSceneObjects] = useState(null);
  const [viewerScenes, setViewerScenes] = useState(null); // full scenes for viewer
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const [threeReady, setThreeReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const viewerRootRef = useRef(null);

  // Callout notes
  const [noteMode, setNoteMode] = useState(false);
  const [notes, setNotes] = useState([]);
  const [hiddenAuthors, setHiddenAuthors] = useState(new Set()); // paydaş anahtar → gizli
  const [filterOpen, setFilterOpen] = useState(false);
  const [noteStep, setNoteStep] = useState(0); // 0:idle 1:placed-target 2:typing
  const pendingRef = useRef(null); // { tx,ty, bx,by }
  const [textareaPos, setTextareaPos] = useState(null); // {x,y} screen coords
  const [editingId, setEditingId] = useState(null);
  const textareaRef = useRef(null);
  const calloutGroupsRef = useRef(new Map()); // id -> THREE.Group
  const [projectBaseH, setProjectBaseH] = useState(0); // scene bounding-box height (stable, zoom-independent)
  const noteIdCounter = useRef(0);
  const previewRef = useRef({ line: null, circle: null });

  // Parse JBZ file
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let buffer;
        if (file) {
          buffer = await file.arrayBuffer();
        } else if (url) {
          const res = await fetch(url);
          buffer = await res.arrayBuffer();
        } else {
          throw new Error('Dosya veya URL gerekli');
        }
        const zip = await JSZip.loadAsync(buffer);

        let fp = null;
        const fpEntry = zip.file('_state/floor-plans.json');
        if (fpEntry) {
          fp = JSON.parse(await fpEntry.async('string'));
          if (!cancelled) setFloorPlans(fp);
        }

        let so = null;
        const soEntry = zip.file('_state/scene-objects.json');
        if (soEntry) {
          so = JSON.parse(await soEntry.async('string'));
          if (!cancelled) setSceneObjects(so);
        }

        // Full viewer-ready scenes (includes DXF objects)
        const vsEntry = zip.file('_state/viewer-scenes.json');
        if (vsEntry) {
          const vs = JSON.parse(await vsEntry.async('string'));
          if (!cancelled) setViewerScenes(vs);
        }

        if (!cancelled) {
          const fpTabIds = fp ? Object.keys(fp.tabs || {}).filter(
            tabId => fp.tabs[tabId]?.allIds?.length > 0
          ) : [];
          if (fpTabIds.length === 0) {
            setError('Çizim verisi bulunamadı');
            setLoading(false);
            return;
          }
          const initialTab = fpTabIds.includes('elektrik')
            ? 'elektrik'
            : fpTabIds.find(id => id !== 'mimari') || fpTabIds[0];
          setActiveTab(initialTab);
          const tabData = fp.tabs?.[initialTab];
          if (tabData) {
            setActiveSheet(tabData.activeId || tabData.allIds?.[0] || null);
          }
        }
      } catch (err) {
        if (!cancelled) setError('JBZ dosyasi okunamadi');
        console.error('JBZ parse error:', err);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [file, url]);

  // Available pafta tabs
  const availableTabs = useMemo(() => {
    if (!floorPlans?.tabs) return [];
    return Object.keys(floorPlans.tabs)
      .filter(tabId => floorPlans.tabs[tabId]?.allIds?.length > 0)
      .sort((a, b) => {
        const ia = TAB_ORDER.indexOf(a);
        const ib = TAB_ORDER.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      })
      .map(tabId => ({ id: tabId, label: FP_TAB_LABELS[tabId] || tabId }));
  }, [floorPlans]);

  const navHeight = availableTabs.length > 0 ? 30 : 0;
  const viewerHeight = height - navHeight;

  // Initialize Three.js — runs once when container is available and not loading
  useEffect(() => {
    const container = containerRef.current;
    if (!container || loading) return;

    let disposed = false;
    let renderer, ro, frameId;

    // Defer to next frame so the browser has completed layout
    const initId = requestAnimationFrame(() => {
      if (disposed) return;

      const w = container.clientWidth || 400;
      const h = viewerHeight || 300;

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setClearColor(0x0a0a10, 1);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(w, h);
      container.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(
        w / -2, w / 2, h / 2, h / -2, -1000, 1000
      );
      camera.position.set(0, 0, 10);
      camera.lookAt(0, 0, 0);

      const animate = () => {
        if (disposed) return;
        renderer.render(scene, camera);
        frameId = requestAnimationFrame(animate);
      };

      threeRef.current = { renderer, scene, camera, frameId: null };
      frameId = requestAnimationFrame(animate);
      setThreeReady(true);

      // ResizeObserver for responsive sizing
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height: h2 } = entry.contentRect;
          if (width > 0 && h2 > 0 && renderer) {
            renderer.setSize(width, h2);
          }
        }
      });
      ro.observe(container);
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(initId);
      if (ro) ro.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      if (renderer) {
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      }
      threeRef.current = null;
      setThreeReady(false);
    };
  }, [loading, viewerHeight]);

  // Build scene when tab/sheet or data changes
  useEffect(() => {
    const three = threeRef.current;
    if (!three || !threeReady || !floorPlans?.tabs || !activeTab) return;
    const { scene, camera, renderer } = three;

    // Clear scene
    while (scene.children.length > 0) {
      const child = scene.children[0];
      scene.remove(child);
    }

    const tabData = floorPlans.tabs[activeTab];
    if (!tabData) return;

    // Check if viewer-scenes has full data for this discipline tab
    const sceneKey = FP_TO_SCENE[activeTab];
    const hasViewerScene = activeTab !== 'mimari' && sceneKey && viewerScenes?.[sceneKey];

    if (hasViewerScene) {
      // Use full viewer scene (includes floor plan + all DXF objects + layout)
      const items = Array.isArray(viewerScenes[sceneKey]) ? viewerScenes[sceneKey] : [viewerScenes[sceneKey]];
      for (const item of items) {
        const obj = deserializeObject(item);
        if (obj) scene.add(obj);
      }
    } else {
      // Fallback: floor plan base + filtered scene-objects
      // 1. Floor plan base (_serializedGroup) — YALNIZCA paftanın KENDİ
      //    serialize edilmiş grubu (mimari tab). Non-mimari paftalarda
      //    _serializedGroup null'dur ve mimari'ye sourceFloorPlanId ile
      //    referans verir; AMA mimari duvarlar mimari-tab dünya konumunda
      //    (origin civarı) kayıtlıyken tesisat elektrik fpMesh konumunda
      //    (uzakta) → cross-tab çözüm burada DUVARLARI YANLIŞ KONUMDA
      //    çizerdi (kamera fit → her şey dağılırdı). Doğru hizalama editörün
      //    fpMesh transform'unu gerektirir; bu yüzden burada YAPILMAZ
      //    (bkz. EditorPreview / iframe editör önizleme).
      const sheetId = activeSheet || tabData.activeId || tabData.allIds?.[0];
      if (sheetId && tabData.byId?.[sheetId]?._serializedGroup) {
        const baseGroup = deserializeFloorPlanGroup(tabData.byId[sheetId]._serializedGroup);
        if (baseGroup) {
          scene.add(baseGroup);
        }
      }

      // 2. Discipline overlay (scene-objects)
      if (activeTab !== 'mimari' && sceneObjects) {
        const data = sceneKey ? sceneObjects[sceneKey] : null;
        if (data) {
          const items = Array.isArray(data) ? data : [data];
          for (const item of items) {
            const obj = deserializeObject(item);
            if (obj) scene.add(obj);
          }
        }
      }
    }

    // 3. Fit camera — double rAF to ensure objects are in scene and layout is done
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!threeRef.current) return;
        const size = new THREE.Vector2();
        renderer.getSize(size);
        const w = size.x || 400;
        const h = size.y || 300;
        const baseH = fitCameraToScene(scene, camera, w, h);
        // Capture the project's natural view height as stable reference for callouts
        if (baseH && baseH > 0) setProjectBaseH(baseH);
      });
    });
  }, [activeTab, activeSheet, floorPlans, sceneObjects, viewerScenes, threeReady]);

  // Mouse pan
  // Hit-test bottom-right corner of any callout's text sprite.
  // Returns { noteId, startScale } if hit, null otherwise.
  const hitTestCalloutHandle = useCallback((worldPt) => {
    const handleFraction = 0.12; // fraction of sprite width/height counted as handle area
    for (const [noteId, group] of calloutGroupsRef.current) {
      const sprite = group.children.find(c => c.isSprite);
      if (!sprite) continue;
      const { x: px, y: py } = sprite.position;
      const w = sprite.scale.x;
      const h = sprite.scale.y;
      // Sprite: center.set(0,1) → top-left is at position
      // Bottom-right corner in world: (px + w, py - h)
      const brx = px + w;
      const bry = py - h;
      const hsW = w * handleFraction;
      const hsH = h * handleFraction;
      if (worldPt.x >= brx - hsW && worldPt.x <= brx &&
          worldPt.y >= bry && worldPt.y <= bry + hsH) {
        return { noteId, startScale: sprite.userData._scale || 1 };
      }
    }
    return null;
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (noteMode && noteStep < 2) return; // don't drag in note placement mode
    e.preventDefault();

    // Check if user is clicking a callout's resize handle
    const three = threeRef.current;
    if (three) {
      const worldPt = screenToWorld(e, three.camera, three.renderer.domElement);
      const hit = hitTestCalloutHandle(worldPt);
      if (hit) {
        const grp = calloutGroupsRef.current.get(hit.noteId);
        const spr = grp?.children.find(c => c.isSprite);
        const startFW = spr?.userData?._frameWidth || 1;
        // World width at frameWidth=1 → unit reference for delta calculation
        const baseWorldW = spr ? spr.scale.x / startFW : 1;
        resizeRef.current = {
          noteId: hit.noteId,
          startWorldX: worldPt.x,
          startFrameWidth: startFW,
          lastFrameWidth: startFW,
          baseWorldW,
          text: spr?.userData?._text,
          header: spr?.userData?._header,
        };
        return;
      }
    }

    dragRef.current = { dragging: true, lastX: e.clientX, lastY: e.clientY };
  }, [noteMode, noteStep, hitTestCalloutHandle]);

  const handleMouseMove = useCallback((e) => {
    // Update preview line endpoint during note placement
    if (noteMode && noteStep === 1 && previewRef.current.line) {
      const three = threeRef.current;
      if (three) {
        const w = screenToWorld(e, three.camera, three.renderer.domElement);
        const posAttr = previewRef.current.line.geometry.getAttribute('position');
        posAttr.setXYZ(1, w.x, w.y, 2);
        posAttr.needsUpdate = true;
        previewRef.current.line.computeLineDistances();
      }
    }

    // Resizing a callout frame (width only — text wraps at new width, font fixed)
    if (resizeRef.current) {
      const three = threeRef.current;
      if (!three) return;
      const { noteId, startWorldX, startFrameWidth, baseWorldW, text, header } = resizeRef.current;
      const worldPt = screenToWorld(e, three.camera, three.renderer.domElement);
      const dxWorld = worldPt.x - startWorldX;
      const deltaFW = dxWorld / baseWorldW;
      let next = startFrameWidth + deltaFW;
      next = Math.max(CALLOUT_MIN_FRAME_WIDTH, Math.min(CALLOUT_MAX_FRAME_WIDTH, next));
      if (Math.abs(next - (resizeRef.current.lastFrameWidth || 0)) < 0.01) return;
      // Rebuild the sprite at the new frame width (font fixed, wrap recomputed).
      const group = calloutGroupsRef.current.get(noteId);
      if (!group) return;
      const oldSprite = group.children.find(c => c.isSprite);
      if (!oldSprite) return;
      const pos = oldSprite.position.clone();
      const viewH = projectBaseH || (three.camera.top - three.camera.bottom) || 300;
      const newSprite = makeTextSprite(text || '', viewH, header || null, next);
      newSprite.position.copy(pos);
      newSprite.renderOrder = 999;
      newSprite.userData = { _noteId: noteId, _frameWidth: next, _text: text, _header: header };
      group.remove(oldSprite);
      if (oldSprite.material.map) oldSprite.material.map.dispose();
      oldSprite.material.dispose();
      group.add(newSprite);
      resizeRef.current.lastFrameWidth = next;
      return;
    }

    if (!dragRef.current.dragging) return;
    const three = threeRef.current;
    if (!three) return;
    const { camera, renderer } = three;

    const rect = renderer.domElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    dragRef.current.lastX = e.clientX;
    dragRef.current.lastY = e.clientY;

    const viewW = camera.right - camera.left;
    const viewH = camera.top - camera.bottom;
    const panX = -(dx / rect.width) * viewW;
    const panY = (dy / rect.height) * viewH;

    camera.left += panX;
    camera.right += panX;
    camera.top += panY;
    camera.bottom += panY;
    camera.updateProjectionMatrix();
  }, [noteMode, noteStep, projectBaseH]);

  const handleMouseUp = useCallback(() => {
    dragRef.current.dragging = false;
    // Persist the final frame width into notes state + server
    if (resizeRef.current && resizeRef.current.lastFrameWidth != null) {
      const { noteId, lastFrameWidth, startFrameWidth } = resizeRef.current;
      if (lastFrameWidth !== startFrameWidth) {
        setNotes(prev => prev.map(n => n.id === noteId ? { ...n, frameWidth: lastFrameWidth } : n));
        if (projectId && !String(noteId).startsWith('note_')) {
          const token = localStorage.getItem('projabze_token');
          fetch(`${API_URL}/api/projects/${projectId}/notes/${noteId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ calloutData: { frameWidth: lastFrameWidth } }),
          }).catch(() => {});
        }
      }
    }
    resizeRef.current = null;
  }, [projectId]);

  // Mouse wheel zoom (toward cursor)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handler = (e) => {
      e.preventDefault();
      const three = threeRef.current;
      if (!three) return;
      const { camera, renderer } = three;

      const rect = renderer.domElement.getBoundingClientRect();
      if (rect.width === 0) return;

      // Normalized cursor position [0..1]
      const mx = (e.clientX - rect.left) / rect.width;
      const my = (e.clientY - rect.top) / rect.height;

      const factor = e.deltaY < 0 ? 0.85 : 1.18;

      // World coords under cursor
      const vw = camera.right - camera.left;
      const vh = camera.top - camera.bottom;
      const wx = camera.left + mx * vw;
      const wy = camera.top - my * vh;

      // Zoom toward cursor
      camera.left = wx - (wx - camera.left) * factor;
      camera.right = wx + (camera.right - wx) * factor;
      camera.top = wy + (camera.top - wy) * factor;
      camera.bottom = wy - (wy - camera.bottom) * factor;
      camera.updateProjectionMatrix();
    };
    container.addEventListener('wheel', handler, { passive: false });
    return () => container.removeEventListener('wheel', handler);
  }, [loading]);

  // Reset view
  const handleReset = useCallback(() => {
    const three = threeRef.current;
    if (!three) return;
    const { scene, camera, renderer } = three;
    const size = new THREE.Vector2();
    renderer.getSize(size);
    const baseH = fitCameraToScene(scene, camera, size.x || 400, size.y || 300);
    if (baseH && baseH > 0) setProjectBaseH(baseH);
  }, []);

  // Zoom in/out buttons
  const handleZoom = useCallback((zoomIn) => {
    const three = threeRef.current;
    if (!three) return;
    const { camera } = three;
    const cx = (camera.left + camera.right) / 2;
    const cy = (camera.top + camera.bottom) / 2;
    const f = zoomIn ? 0.77 : 1.3;
    const hw = (camera.right - camera.left) / 2 * f;
    const hh = (camera.top - camera.bottom) / 2 * f;
    camera.left = cx - hw;
    camera.right = cx + hw;
    camera.top = cy + hh;
    camera.bottom = cy - hh;
    camera.updateProjectionMatrix();
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const tabData = floorPlans?.tabs?.[tabId];
    if (tabData) {
      setActiveSheet(tabData.activeId || tabData.allIds?.[0] || null);
    } else {
      setActiveSheet(null);
    }
  };

  // Dış API: bir notun targetPoint'ine kamerayı odakla. zoomTarget prop'u
  // her tıklamada yeni bir `ts` ile geldiği için ts değiştiğinde efekt
  // tetiklenir. tabId varsa önce sekmeyi değiştirir, sonra kamerayı
  // dünya koordinatına yakınlaştırır.
  const pendingZoomRef = useRef(null);
  useEffect(() => {
    if (!zoomTarget || !zoomTarget.point) return;
    pendingZoomRef.current = zoomTarget;
    if (zoomTarget.tabId && activeTab !== zoomTarget.tabId) {
      handleTabChange(zoomTarget.tabId);
      return; // kamera odaklaması, activeTab güncellendiğinde aşağıdaki branch'e düşer
    }
  }, [zoomTarget?.ts]); // eslint-disable-line react-hooks/exhaustive-deps

  // activeTab değiştiğinde, bekleyen bir zoom hedefi varsa ve tabId uyuyorsa
  // kamerayı odakla. Sahnenin yeniden çizilmesi için iki rAF bekleriz.
  useEffect(() => {
    const target = pendingZoomRef.current;
    if (!target || !target.point || !threeRef.current) return;
    if (target.tabId && target.tabId !== activeTab) return;
    const { camera } = threeRef.current;
    const focus = () => {
      const { x, y } = target.point;
      // Hedef civarında dar bir görünüm — kullanıcı wheel/zoom ile ayarlayabilir.
      const aspect = (camera.right - camera.left) / Math.max(1e-6, (camera.top - camera.bottom));
      const viewW = 400;
      const viewH = viewW / Math.max(1e-6, aspect);
      camera.left   = x - viewW / 2;
      camera.right  = x + viewW / 2;
      camera.top    = y + viewH / 2;
      camera.bottom = y - viewH / 2;
      camera.updateProjectionMatrix();
      pendingZoomRef.current = null;
    };
    requestAnimationFrame(() => requestAnimationFrame(focus));
  }, [activeTab, zoomTarget?.ts]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // ESC to exit fullscreen / cancel note
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (filterOpen) { setFilterOpen(false); return; }
        if (noteStep > 0) {
          // cancel note creation
          setNoteStep(0);
          setTextareaPos(null);
          setEditingId(null);
          pendingRef.current = null;
          return;
        }
        if (isFullscreen) setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullscreen, noteStep, filterOpen]);

  // Close filter dropdown when clicking outside the viewer controls
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (!e.target.closest('.jbz-filter-wrap')) setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  // ─── Callout note rendering ────────────────────────────────────────────────

  // Sync callout groups in scene with notes array (filtered by paydaş)
  useEffect(() => {
    const three = threeRef.current;
    if (!three || !threeReady) return;
    const { scene, camera } = three;
    // Proje referans yüksekliği — callout boyutu bunun sabit yüzdesi olsun.
    const viewH = projectBaseH || (camera.top - camera.bottom) || 300;

    // ── Per-stakeholder index (numbering) tüm notlar üzerinden hesaplanır,
    //    gizli olanlar dahil — böylece görünürlük değişince numaralar kaymaz.
    const sorted = [...notes].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (ta !== tb) return ta - tb;
      return (a.id || '').localeCompare(b.id || '');
    });
    const authorCounts = {};
    const indexedNotes = sorted.map(n => {
      const key = `${n.authorName || ''}|${n.authorRole || ''}`;
      authorCounts[key] = (authorCounts[key] || 0) + 1;
      return { ...n, _authorIndex: authorCounts[key] };
    });

    // Seçili paydaşları filtrele (gizli olanlar render edilmez)
    const visibleNotes = indexedNotes.filter(n => {
      const key = `${n.authorName || 'Anonim'}|${n.authorRole || ''}`;
      return !hiddenAuthors.has(key);
    });
    const visibleIds = new Set(visibleNotes.map(n => n.id));

    // Remove any group not in visible set (stale or hidden)
    for (const [id, g] of calloutGroupsRef.current) {
      if (!visibleIds.has(id)) {
        scene.remove(g);
        calloutGroupsRef.current.delete(id);
      }
    }
    // Add / update groups for visible notes
    for (const note of visibleNotes) {
      const existing = calloutGroupsRef.current.get(note.id);
      if (existing) {
        scene.remove(existing);
        calloutGroupsRef.current.delete(note.id);
      }
      const g = buildCalloutGroup(note, viewH);
      scene.add(g);
      calloutGroupsRef.current.set(note.id, g);
    }
  }, [notes, threeReady, projectBaseH, hiddenAuthors]);

  // Preview line + circle while placing note (step 1)
  useEffect(() => {
    const three = threeRef.current;
    if (!three || !threeReady) return;
    const { scene, camera } = three;

    // Clean up any existing preview
    const cleanPreview = () => {
      if (previewRef.current.line) {
        scene.remove(previewRef.current.line);
        previewRef.current.line.geometry.dispose();
        previewRef.current.line.material.dispose();
        previewRef.current.line = null;
      }
      if (previewRef.current.circle) {
        scene.remove(previewRef.current.circle);
        previewRef.current.circle.geometry.dispose();
        previewRef.current.circle.material.dispose();
        previewRef.current.circle = null;
      }
    };

    cleanPreview();

    if (noteStep === 1 && pendingRef.current) {
      const { tx, ty } = pendingRef.current;
      const viewH = camera.top - camera.bottom || 300;

      // Preview circle at target point — sabit 2 birim yarıçap
      const mr = 2;
      const cg = new THREE.CircleGeometry(mr, 16);
      const cm = new THREE.MeshBasicMaterial({ color: CALLOUT_COLOR, depthTest: false, transparent: true, opacity: 0.8 });
      const circle = new THREE.Mesh(cg, cm);
      circle.position.set(tx, ty, 2);
      circle.renderOrder = 1000;
      scene.add(circle);
      previewRef.current.circle = circle;

      // Preview dashed line (endpoint updated on mousemove)
      const positions = new Float32Array([tx, ty, 2, tx, ty, 2]);
      const lg = new THREE.BufferGeometry();
      lg.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const lm = new THREE.LineDashedMaterial({
        color: CALLOUT_COLOR, depthTest: false, transparent: true, opacity: 0.6,
        dashSize: viewH * 0.006, gapSize: viewH * 0.004,
      });
      const line = new THREE.Line(lg, lm);
      line.computeLineDistances();
      line.renderOrder = 1000;
      scene.add(line);
      previewRef.current.line = line;
    }

    return cleanPreview;
  }, [noteStep, threeReady]);

  // Note tool click handler
  const handleNoteClick = useCallback((e) => {
    if (!noteMode) return;
    const three = threeRef.current;
    if (!three) return;
    const { camera, renderer } = three;
    const w = screenToWorld(e, camera, renderer.domElement);

    if (noteStep === 0) {
      // First click: target point
      const viewH = camera.top - camera.bottom || 300;
      const offset = viewH * 0.08;
      pendingRef.current = { tx: w.x, ty: w.y, bx: w.x + offset, by: w.y + offset };
      setNoteStep(1);
    } else if (noteStep === 1) {
      // Second click: box position
      pendingRef.current.bx = w.x;
      pendingRef.current.by = w.y;
      // Show textarea at screen position
      const rect = renderer.domElement.getBoundingClientRect();
      setTextareaPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      setNoteStep(2);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [noteMode, noteStep]);

  // Double-click to edit existing callout
  const handleNoteDblClick = useCallback((e) => {
    const three = threeRef.current;
    if (!three) return;
    const { camera, renderer } = three;
    const w = screenToWorld(e, camera, renderer.domElement);
    const viewH = camera.top - camera.bottom || 300;
    const threshold = viewH * 0.1;

    // Find closest callout
    let closest = null;
    let minDist = Infinity;
    for (const note of notes) {
      const dx = w.x - note.bx;
      const dy = w.y - note.by;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < threshold && dist < minDist) {
        minDist = dist;
        closest = note;
      }
    }
    if (!closest) return;

    e.stopPropagation();
    setEditingId(closest.id);
    setNoteStep(2);
    pendingRef.current = { tx: closest.tx, ty: closest.ty, bx: closest.bx, by: closest.by };
    const rect = renderer.domElement.getBoundingClientRect();
    // Convert world to screen
    const nx = (closest.bx - camera.left) / (camera.right - camera.left);
    const ny = (camera.top - closest.by) / (camera.top - camera.bottom);
    setTextareaPos({ x: nx * rect.width, y: ny * rect.height });
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.value = closest.text;
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    }, 50);
  }, [notes]);

  // API helpers for notes
  const notesApiHeaders = useCallback(() => {
    const token = localStorage.getItem('projabze_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  // Load notes from API on mount / tab change + canlı poll (5s).
  // Diğer paydaşlar yeni not eklediğinde önizlemede de anında görünür.
  useEffect(() => {
    if (!projectId || !activeTab) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}/notes`, {
          headers: notesApiHeaders(),
        });
        if (!res.ok) return;
        const { notes: serverNotes } = await res.json();
        if (cancelled) return;
        const mapped = (serverNotes || [])
          .filter(n => (n.tabId || null) === activeTab)
          .map(n => ({
            id: n.id,
            text: n.rawText || '',
            tx: n.targetPoint?.x ?? 0,
            ty: n.targetPoint?.y ?? 0,
            bx: n.boxPosition?.x ?? 0,
            by: n.boxPosition?.y ?? 0,
            frameWidth: typeof n.frameWidth === 'number' ? n.frameWidth : (typeof n.scale === 'number' ? n.scale : 1),
            authorName: n.authorName,
            authorRole: n.authorRole,
            authorAvatar: n.authorAvatar,
            createdAt: n.createdAt,
            updatedAt: n.updatedAt,
            _server: true,
          }));
        // Mevcut local-only notları (henüz server'a yazılmamış) merge et
        setNotes(prev => {
          const localOnly = (prev || []).filter(n => !n._server);
          return [...mapped, ...localOnly];
        });
      } catch (_) { /* offline — keep local notes */ }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [projectId, activeTab, notesApiHeaders]);

  const handleNoteSubmit = useCallback(() => {
    const text = textareaRef.current?.value?.trim();
    if (!text || !pendingRef.current) {
      setNoteStep(0);
      setTextareaPos(null);
      setEditingId(null);
      pendingRef.current = null;
      return;
    }
    const p = pendingRef.current;
    if (editingId) {
      // Update existing
      setNotes(prev => prev.map(n => n.id === editingId ? { ...n, text, tx: p.tx, ty: p.ty, bx: p.bx, by: p.by } : n));
      // Persist update to API
      if (projectId) {
        fetch(`${API_URL}/api/projects/${projectId}/notes/${editingId}`, {
          method: 'PATCH',
          headers: notesApiHeaders(),
          body: JSON.stringify({
            calloutData: {
              rawText: text,
              targetPoint: { x: p.tx, y: p.ty },
              boxPosition: { x: p.bx, y: p.by },
              tabId: activeTab,
            },
          }),
        }).catch(() => {});
      }
    } else {
      // Create new — optimistic local add, then sync with server ID
      const tempId = 'note_' + (++noteIdCounter.current) + '_' + Date.now();
      const newNote = { id: tempId, text, tx: p.tx, ty: p.ty, bx: p.bx, by: p.by };
      setNotes(prev => [...prev, newNote]);

      if (projectId) {
        fetch(`${API_URL}/api/projects/${projectId}/notes`, {
          method: 'POST',
          headers: notesApiHeaders(),
          body: JSON.stringify({
            calloutData: {
              rawText: text,
              targetPoint: { x: p.tx, y: p.ty },
              boxPosition: { x: p.bx, y: p.by },
              tabId: activeTab,
              calloutId: tempId,
            },
          }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.note?.id) {
              // Replace temp ID with server ID
              setNotes(prev => prev.map(n => n.id === tempId ? { ...n, id: data.note.id, _server: true } : n));
            }
          })
          .catch(() => {});
      }
    }
    setNoteStep(0);
    setTextareaPos(null);
    setEditingId(null);
    pendingRef.current = null;
  }, [editingId, projectId, activeTab, notesApiHeaders]);

  const handleNoteDelete = useCallback((id) => {
    setNotes(prev => prev.filter(n => n.id !== id));
    if (projectId) {
      fetch(`${API_URL}/api/projects/${projectId}/notes/${id}`, {
        method: 'DELETE',
        headers: notesApiHeaders(),
      }).catch(() => {});
    }
  }, [projectId, notesApiHeaders]);

  const handleNoteTextKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNoteSubmit();
    }
    if (e.key === 'Escape') {
      setNoteStep(0);
      setTextareaPos(null);
      setEditingId(null);
      pendingRef.current = null;
    }
  }, [handleNoteSubmit]);

  if (loading) {
    return (
      <div className={`jbz-viewer jbz-viewer-loading ${className}`} style={{ height }}>
        <div className="jbz-loading-spinner"></div>
        <span>JBZ dosyası yükleniyor...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`jbz-viewer jbz-viewer-error ${className}`} style={{ height }}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div ref={viewerRootRef} className={`jbz-viewer ${className} ${isFullscreen ? 'jbz-fullscreen' : ''}`}>
      {availableTabs.length > 0 && (
        <div className="jbz-nav">
          <div className="jbz-nav-tabs">
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                className={`jbz-nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div
        ref={containerRef}
        className="jbz-three-container"
        style={{
          height: isFullscreen ? undefined : viewerHeight,
          cursor: noteMode && noteStep < 2 ? 'crosshair' : dragRef.current.dragging ? 'grabbing' : 'grab',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleNoteClick}
        onDoubleClick={handleNoteDblClick}
      >
        <div className="dxf-viewer-controls">
          <button onClick={() => handleZoom(true)} title="Yakinlastir">+</button>
          <button onClick={() => handleZoom(false)} title="Uzaklastir">−</button>
          <button onClick={handleReset} title="Sifirla">⟳</button>
          <button
            className={noteMode ? 'active' : ''}
            onClick={(e) => { e.stopPropagation(); setNoteMode(m => !m); setNoteStep(0); setTextareaPos(null); pendingRef.current = null; }}
            title={noteMode ? 'Not modundan cik' : 'Not ekle'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          </button>
          {notes.length > 0 && (() => {
            const authorMap = new Map();
            for (const n of notes) {
              const key = `${n.authorName || 'Anonim'}|${n.authorRole || ''}`;
              if (!authorMap.has(key)) {
                authorMap.set(key, { key, name: n.authorName || 'Anonim', role: n.authorRole || null, count: 0 });
              }
              authorMap.get(key).count++;
            }
            const authors = [...authorMap.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr'));
            const anyHidden = authors.some(a => hiddenAuthors.has(a.key));
            return (
              <div className="jbz-filter-wrap" style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  className={anyHidden ? 'active' : ''}
                  onClick={(e) => { e.stopPropagation(); setFilterOpen(o => !o); }}
                  title={anyHidden ? `${authors.filter(a => hiddenAuthors.has(a.key)).length} paydaş gizli` : 'Paydaş filtresi'}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
                </button>
                {filterOpen && (
                  <div className="jbz-filter-dropdown" onClick={e => e.stopPropagation()}>
                    <div className="jbz-filter-header">
                      <span>Paydaş Notları</span>
                      <div className="jbz-filter-actions">
                        <button onClick={() => setHiddenAuthors(new Set())}>Hepsi</button>
                        <button onClick={() => setHiddenAuthors(new Set(authors.map(a => a.key)))}>Hiçbiri</button>
                      </div>
                    </div>
                    <div className="jbz-filter-list">
                      {authors.map(a => {
                        const hidden = hiddenAuthors.has(a.key);
                        return (
                          <label key={a.key} className="jbz-filter-item">
                            <input
                              type="checkbox"
                              checked={!hidden}
                              onChange={() => setHiddenAuthors(prev => {
                                const next = new Set(prev);
                                if (next.has(a.key)) next.delete(a.key); else next.add(a.key);
                                return next;
                              })}
                            />
                            <span className="jbz-filter-name">{a.name}</span>
                            {a.role && <span className="jbz-filter-role">{a.role}</span>}
                            <span className="jbz-filter-count">{a.count}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <button onClick={toggleFullscreen} title={isFullscreen ? 'Tam ekrandan cik (ESC)' : 'Tam ekran'}>
            {isFullscreen ? '⤓' : '⤢'}
          </button>
        </div>
        {/* Note textarea overlay */}
        {noteStep === 2 && textareaPos && (() => {
          const editing = editingId ? notes.find(n => n.id === editingId) : null;
          const fmtDate = (iso) => {
            try {
              return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            } catch { return ''; }
          };
          const initials = editing?.authorName
            ? editing.authorName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
            : '?';
          return (
            <div className="jbz-note-input" style={{ left: textareaPos.x, top: textareaPos.y }}>
              {editing && editing.authorName && (
                <div className="jbz-note-header">
                  <div className="jbz-note-author">
                    {editing.authorAvatar
                      ? <img className="jbz-note-avatar" src={API_URL + editing.authorAvatar} alt="" />
                      : <span className="jbz-note-avatar jbz-note-avatar-initials">{initials}</span>
                    }
                    <div className="jbz-note-author-info">
                      <span className="jbz-note-author-name">{editing.authorName}</span>
                      {editing.authorRole && <span className="jbz-note-author-role">{editing.authorRole}</span>}
                    </div>
                  </div>
                  {editing.createdAt && (
                    <span className="jbz-note-date" title={editing.updatedAt && editing.updatedAt !== editing.createdAt ? `Güncellendi: ${fmtDate(editing.updatedAt)}` : ''}>
                      {fmtDate(editing.createdAt)}
                    </span>
                  )}
                </div>
              )}
              <textarea
                ref={textareaRef}
                rows={3}
                placeholder="Not yaz... (Enter kaydet, Esc iptal)"
                onKeyDown={handleNoteTextKeyDown}
                onBlur={handleNoteSubmit}
              />
              {editingId && (
                <button className="jbz-note-delete" onClick={(e) => { e.stopPropagation(); handleNoteDelete(editingId); setNoteStep(0); setTextareaPos(null); setEditingId(null); }}>
                  Sil
                </button>
              )}
            </div>
          );
        })()}
        {noteMode && noteStep === 0 && <div className="dxf-viewer-hint" style={{ left: 10, right: 'auto', color: CALLOUT_CSS }}>Hedef noktaya tikla</div>}
        {noteMode && noteStep === 1 && <div className="dxf-viewer-hint" style={{ left: 10, right: 'auto', color: CALLOUT_CSS }}>Kutu konumuna tikla</div>}
        {!noteMode && isFullscreen && <div className="dxf-viewer-hint" style={{ left: 10, right: 'auto' }}>ESC ile cik</div>}
        {!noteMode && !isFullscreen && <div className="dxf-viewer-hint">Kaydirmak icin surukle, zum icin scroll</div>}
      </div>
    </div>
  );
};

export default JbzViewer;
